import { CompanyContainer } from "components/company/v3/CompanyContainers";
import CompanyLocalState, { CompanyEventsLocalState } from "components/company/v3/CompanyLocalState";
import AmericanAboutBox from "components/company/v3/american/boxes/AmericanAboutBox";
import AmericanDiscussionsBox from "components/company/v3/american/boxes/AmericanDiscussionsBox";
import AmericanEventsBox from "components/company/v3/american/boxes/AmericanEventsBox";
import AmericanFinancialsBox from "components/company/v3/american/boxes/AmericanFinancialsBox";
import AmericanGovernanceBox from "components/company/v3/american/boxes/AmericanGovernanceBox";
import AmericanHighlightsBox from "components/company/v3/american/boxes/AmericanHighlightsBox";
import AmericanKpisBox from "components/company/v3/american/boxes/AmericanKpisBox";
import AmericanLiquidityBox from "components/company/v3/american/boxes/AmericanLiquidityBox";
import OperatingSegmentsBox from "components/company/v3/boxes/OperatingSegmentsBox";
import AmericanQuotesBox from "components/company/v3/american/boxes/AmericanQuotesBox";
import AmericanCardsContent from "components/company/v3/american/contents/about/AmericanCardsContent";
import {
  CompanyDiscussionsTabEnum,
  CompanyFinancialsTabEnum,
  CompanyGovernanceTabEnum,
  CompanyHighlightsTabEnum,
  CompanyLiquidityTabEnum,
  CompanyMenuSlugEnum,
  CompanyOperatingSegmentsTabEnum,
  CompanyQuotesTabEnum,
  KpisTabEnum,
} from "components/company/v3/menu/enums";
import type { CompanyMenuSlug } from "components/company/v3/menu/types";
import type { UserSettingsShape } from "components/settings/UserSettingsSchema";
import { useSpecificSetting } from "components/settings/hooks/useSpecificSetting";
import type { Selfie } from "lib/hooks/useSelfie";
import useSelfie from "lib/hooks/useSelfie";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";

export default function useAmericanBoxes(tickerSymbol: string | null) {
  const viewer = useSelfie();
  const { value: enabledIssuerPageBoxes } = useSpecificSetting("enabledIssuerPageBoxes.stocks");

  if (!tickerSymbol) {
    return {
      orderedMenu: [],
    };
  }

  if (
    !enabledIssuerPageBoxes ||
    (enabledIssuerPageBoxes && Array.isArray(enabledIssuerPageBoxes) && enabledIssuerPageBoxes.length === 0)
  ) {
    const systemDefaultOrder: UserSettingsShape["enabledIssuerPageBoxes"]["stocks"] = [
      CompanyMenuSlugEnum.ABOUT,
      CompanyMenuSlugEnum.CARDS,
      CompanyMenuSlugEnum.QUOTES,
      CompanyMenuSlugEnum.FINANCIALS,
    ];

    return {
      orderedMenu: orderMenuBoxesByPreferences(
        viewer,
        tickerSymbol.toUpperCase(),
        systemDefaultOrder as CompanyMenuSlug[],
      ),
    };
  }

  return {
    orderedMenu: orderMenuBoxesByPreferences(
      viewer,
      tickerSymbol.toUpperCase(),
      enabledIssuerPageBoxes as CompanyMenuSlug[],
    ),
  };
}

function orderMenuBoxesByPreferences(viewer: Selfie, tickerSymbol: string, preferredOrder: CompanyMenuSlug[]) {
  if (viewer?.isPremium) {
    const boxes = [
      {
        slug: CompanyMenuSlugEnum.ABOUT,
        box: <AmericanAboutBox tickerSymbol={tickerSymbol} />,
      },
      {
        slug: CompanyMenuSlugEnum.CARDS,
        box: (
          <CompanyContainer className="xl:h-full">
            <AmericanCardsContent />
          </CompanyContainer>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.GOVERNANCE,
        box: (
          <CompanyLocalState<CompanyGovernanceTabEnum> defaultTab={CompanyGovernanceTabEnum.COMPENSATION}>
            {(props) => <AmericanGovernanceBox tickerSymbol={tickerSymbol} {...props} />}
          </CompanyLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.LIQUIDITY,
        box: (
          <CompanyLocalState<CompanyLiquidityTabEnum> defaultTab={CompanyLiquidityTabEnum.TRADE_HISTORY}>
            {(props) => <AmericanLiquidityBox tickerSymbol={tickerSymbol} {...props} />}
          </CompanyLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.OPERATING_SEGMENTS,
        box: (
          <CompanyLocalState<CompanyOperatingSegmentsTabEnum> defaultTab={CompanyOperatingSegmentsTabEnum.BUSINESS}>
            {(props) => <OperatingSegmentsBox tickerSymbol={tickerSymbol} {...props} />}
          </CompanyLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.KPIS,
        box: (
          <CompanyLocalState<KpisTabEnum> defaultTab={KpisTabEnum.TABLE}>
            {(props) => <AmericanKpisBox tickerSymbol={tickerSymbol} {...props} />}
          </CompanyLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.FINANCIALS,
        box: (
          <CompanyLocalState<CompanyFinancialsTabEnum> defaultTab={CompanyFinancialsTabEnum.FINANCIALS}>
            {(props) => <AmericanFinancialsBox tickerSymbol={tickerSymbol} {...props} />}
          </CompanyLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.QUOTES,
        box: (
          <CompanyLocalState<CompanyQuotesTabEnum> defaultTab={CompanyQuotesTabEnum.QUOTE}>
            {(props) => <AmericanQuotesBox tickerSymbol={tickerSymbol} {...props} />}
          </CompanyLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.EVENTS,
        box: (
          <CompanyEventsLocalState>
            {(props) => <AmericanEventsBox tickerSymbol={tickerSymbol} {...props} isRenderPageDefault={true} />}
          </CompanyEventsLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.HIGHLIGHTS,
        box: (
          <CompanyLocalState<CompanyHighlightsTabEnum> defaultTab={CompanyHighlightsTabEnum.VIDEOS}>
            {(props) => <AmericanHighlightsBox {...props} />}
          </CompanyLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.DISCUSSIONS,
        box: (
          <CompanyLocalState<CompanyDiscussionsTabEnum> defaultTab={CompanyDiscussionsTabEnum.COMMENTS}>
            {(props) => <AmericanDiscussionsBox tickerSymbol={tickerSymbol} {...props} />}
          </CompanyLocalState>
        ),
      },
    ].filter(notUndefinedOrNull);

    return preferredOrder.map((slug) => boxes.find((box) => box.slug === slug)).filter(notUndefinedOrNull);
  }

  const boxes = [
    {
      slug: CompanyMenuSlugEnum.ABOUT,
      box: <AmericanAboutBox tickerSymbol={tickerSymbol} />,
    },
    {
      slug: CompanyMenuSlugEnum.CARDS,
      box: (
        <CompanyContainer className="xl:h-full">
          <AmericanCardsContent />
        </CompanyContainer>
      ),
    },
    {
      slug: CompanyMenuSlugEnum.KPIS,
      box: (
        <CompanyLocalState<KpisTabEnum> defaultTab={KpisTabEnum.TABLE}>
          {(props) => <AmericanKpisBox tickerSymbol={tickerSymbol} {...props} />}
        </CompanyLocalState>
      ),
    },
    {
      slug: CompanyMenuSlugEnum.FINANCIALS,
      box: (
        <CompanyLocalState<CompanyFinancialsTabEnum> defaultTab={CompanyFinancialsTabEnum.FINANCIALS}>
          {(props) => <AmericanFinancialsBox tickerSymbol={tickerSymbol} {...props} />}
        </CompanyLocalState>
      ),
    },
    {
      slug: CompanyMenuSlugEnum.QUOTES,
      box: (
        <CompanyLocalState<CompanyQuotesTabEnum> defaultTab={CompanyQuotesTabEnum.QUOTE}>
          {(props) => <AmericanQuotesBox tickerSymbol={tickerSymbol} {...props} />}
        </CompanyLocalState>
      ),
    },
    {
      slug: CompanyMenuSlugEnum.EVENTS,
      box: (
        <CompanyEventsLocalState>
          {(props) => <AmericanEventsBox tickerSymbol={tickerSymbol} {...props} isRenderPageDefault={true} />}
        </CompanyEventsLocalState>
      ),
    },
  ].filter(notUndefinedOrNull);

  return preferredOrder.map((slug) => boxes.find((box) => box.slug === slug)).filter(notUndefinedOrNull);
}
